/**
 * Condizioni di trasporto: Porto, Trasporto a cura del, Vettore
 * (`.scratch/condizioni-trasporto/spec.md`; termini in `CONTEXT.md`,
 * *Spedizione*). Il backend rende i codici NTS, qui le etichette e quando
 * si mostrano.
 */

export interface EtichettaPorto {
  etichetta: string;
  /** Chi paga la spedizione; null per i porti senza etichetta nostra. */
  tooltip: string | null;
}

/** Le righe da mostrare; una riga assente è null. */
export interface CondizioniTrasportoDati {
  porto: EtichettaPorto | null;
  trasportoACuraDel: string | null;
  vettore: string | null;
}

/** Codici di `tabport` (ENV-30). */
export const PORTO_FRANCO = '1';
export const PORTO_ASSEGNATO = '3';
export const PORTO_FRANCO_ADDEBITO = '4';

const PORTI: Record<string, EtichettaPorto> = {
  [PORTO_FRANCO]: { etichetta: 'Porto Franco', tooltip: 'La spedizione è a carico di DTK.' },
  [PORTO_ASSEGNATO]: {
    etichetta: 'Porto Assegnato',
    tooltip: 'La spedizione è a carico del cliente, con il suo vettore.',
  },
  [PORTO_FRANCO_ADDEBITO]: {
    etichetta: 'Porto Franco Add. In Fattura',
    tooltip: 'DTK spedisce e addebita il trasporto in fattura.',
  },
};

/** `an_acuradi` / `tm_acuradi` (API-110); X ("eredita") e vuoto non si mostrano. */
const A_CURA_DI: Record<string, string> = { M: 'Mittente', D: 'Destinatario', V: 'Vettore' };

const codice = (v: unknown): string => (v == null ? '' : String(v).trim());

/** Etichetta e tooltip del porto `codice`; per gli altri codici la
 *  descrizione di NTS (es. 2 "EX WORKS"), senza tooltip. */
export function etichettaPorto(cod: unknown, descrizioneNts?: string | null): EtichettaPorto | null {
  const c = codice(cod);
  if (PORTI[c]) return PORTI[c];
  const descr = (descrizioneNts || '').trim();
  return descr ? { etichetta: descr, tooltip: null } : null;
}

export function etichettaACuraDel(cod: unknown): string | null {
  return A_CURA_DI[codice(cod).toUpperCase()] ?? null;
}

const haVettore = (cod: unknown): boolean => {
  const n = Number(cod);
  return Number.isFinite(n) && n > 0;
};

/** Riquadro "Condizioni commerciali" della Panoramica: Trasporto a cura del
 *  sempre (se M, D o V); Porto e Vettore 1 solo per il Porto Assegnato. */
export function condizioniCliente(ana: {
  porto?: string | number | null;
  portoLabel?: string | null;
  trasportoACuraDel?: string | null;
  vettoreCodice?: number | null;
  vettoreLabel?: string | null;
  vettorePreferenziale?: boolean | null;
} | null | undefined): CondizioniTrasportoDati {
  const assegnato = codice(ana?.porto) === PORTO_ASSEGNATO;
  const vettore = assegnato && haVettore(ana?.vettoreCodice) && ana?.vettoreLabel
    ? `${ana.vettoreLabel}${ana.vettorePreferenziale ? ' (preferenziale)' : ''}`
    : null;
  return {
    porto: assegnato ? etichettaPorto(ana?.porto, ana?.portoLabel) : null,
    trasportoACuraDel: etichettaACuraDel(ana?.trasportoACuraDel),
    vettore,
  };
}

/** Dettaglio di un documento: Porto con qualunque codice, Trasporto a cura
 *  del, il Vettore che ha spedito. */
export function condizioniDocumento(cond: {
  portoCodice?: string | null;
  porto?: string | null;
  trasportoACuraDel?: string | null;
  vettore?: string | null;
} | null | undefined): CondizioniTrasportoDati {
  return {
    porto: etichettaPorto(cond?.portoCodice, cond?.porto),
    trasportoACuraDel: etichettaACuraDel(cond?.trasportoACuraDel),
    vettore: (cond?.vettore || '').trim() || null,
  };
}
